import { useEffect, useRef, useState } from 'react';
import type { AiServiceKind, AiServiceSource, AiServiceTestResultDTO, EmployeeAiServiceInputDTO, EmployeeAiSettingsDTO } from '@wberp/shared';
import { api } from './api';
import { ErrorBox, Loading, Pill } from './components';

type Draft = EmployeeAiServiceInputDTO & { source: AiServiceSource };
function toDraft(value: EmployeeAiSettingsDTO[AiServiceKind]): Draft {
  return { provider: value.provider, baseUrl: value.baseUrl, model: value.model, source: value.source };
}
function signature(draft: Draft) { return JSON.stringify([draft.provider, draft.baseUrl, draft.model, draft.apiKey ?? '']); }
function missingPersonalFields(draft: Draft, hasSecret: boolean): string[] {
  return [!draft.baseUrl?.trim() ? 'API Base URL' : '', !draft.model?.trim() ? '模型' : '',
    !(draft.apiKey?.trim() || hasSecret) ? 'API Key' : ''].filter(Boolean);
}

export function EmployeeAiSettings() {
  const [settings, setSettings] = useState<EmployeeAiSettingsDTO | null>(null);
  const [drafts, setDrafts] = useState<Record<AiServiceKind, Draft> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<AiServiceKind | 'save' | null>(null);
  const [reload, setReload] = useState(0);
  const [saveStatus, setSaveStatus] = useState<{ message: string; complete: boolean } | null>(null);
  const [results, setResults] = useState<Partial<Record<AiServiceKind, { signature: string; value: AiServiceTestResultDTO }>>>({});
  const currentDrafts = useRef(drafts);
  currentDrafts.current = drafts;
  const locked = useRef(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null); setSaveStatus(null);
    api.getEmployeeAiSettings().then(value => {
      if (!active) return;
      setError(null); setSettings(value); setDrafts({ text: toDraft(value.text), image: toDraft(value.image) }); setResults({});
    }).catch(reason => { if (active) setError(reason instanceof Error ? reason : new Error('AI 配置读取失败')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);
  useEffect(() => {
    let active = true;
    const refreshStatus = () => { void api.getEmployeeAiSettings().then(value => { if (active) setSettings(value); }).catch(() => undefined); };
    window.addEventListener('focus', refreshStatus);
    return () => { active = false; window.removeEventListener('focus', refreshStatus); };
  }, []);

  function update(kind: AiServiceKind, key: keyof Draft, value: string) {
    setDrafts(current => current ? { ...current, [kind]: { ...current[kind], [key]: value || (key === 'apiKey' ? undefined : null) } } : current);
    setSaveStatus(null);
    if (key !== 'source') setResults(current => ({ ...current, [kind]: undefined }));
  }
  async function testService(kind: AiServiceKind) {
    if (!drafts || locked.current) return;
    locked.current = true; setBusy(kind); setError(null);
    const snapshot = { ...drafts[kind] };
    const expected = signature(snapshot);
    setSaveStatus(null);
    try {
      const value = await api.testEmployeeAi({ kind, provider: snapshot.provider, baseUrl: snapshot.baseUrl, model: snapshot.model,
        ...(snapshot.apiKey?.trim() ? { apiKey: snapshot.apiKey } : {}) });
      if (signature(currentDrafts.current![kind]) === expected) setResults(current => ({ ...current, [kind]: { signature: expected, value } }));
      if (value.saved) {
        const saved = await api.getEmployeeAiSettings();
        setSettings(saved);
        // Keep the other draft and any unsaved source selection intact.
        if (signature(currentDrafts.current![kind]) === expected) {
          setResults(current => ({ ...current, [kind]: { signature: signature(toDraft(saved[kind])), value } }));
        }
        setDrafts(current => current && signature(current[kind]) === expected
          ? { ...current, [kind]: { ...toDraft(saved[kind]), source: current[kind].source } } : current);
      }
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('AI 服务测试失败')); }
    finally { locked.current = false; setBusy(null); }
  }
  async function saveDefaults() {
    if (!drafts || !settings || locked.current) return;
    for (const kind of ['text', 'image'] as const) {
      const missing = missingPersonalFields(drafts[kind], settings[kind].hasSecret);
      if (settings[kind].shared && drafts[kind].source === 'personal' && missing.length) {
        setSaveStatus(null);
        setError(new Error(`${kind === 'text' ? '文字 AI' : '生图'}配置不完整，请补齐${missing.join('、')}后再选择自己的服务`));
        return;
      }
    }
    locked.current = true; setBusy('save'); setError(null); setSaveStatus(null);
    const snapshots = { text: { ...drafts.text }, image: { ...drafts.image } };
    let completed = 0;
    let savingKind: AiServiceKind = 'text';
    try {
      for (const kind of ['text', 'image'] as const) {
        savingKind = kind;
        const snapshot = snapshots[kind], expected = signature(snapshot);
        const saved = await api.putEmployeeAiService(kind, snapshot);
        setSettings(saved);
        setDrafts(current => current && signature(current[kind]) === expected && current[kind].source === snapshot.source
          ? { ...current, [kind]: toDraft(saved[kind]) } : current);
        setResults(current => ({ ...current, [kind]: undefined }));
        completed++;
      }
      setSaveStatus({ complete: true, message: '模型默认值已保存；后续调用使用当前来源，相关任务将按需分批恢复。' });
    } catch (reason) {
      setError(reason instanceof Error ? reason : new Error('AI 配置保存失败'));
      setSaveStatus({ complete: false, message: `${savingKind === 'text' ? '文字 AI' : '生图'}配置保存失败；${completed ? '文字 AI 配置已保存。' : ''}未保存内容已保留，请重试保存默认值。` });
    }
    finally { locked.current = false; setBusy(null); }
  }

  return <div className="stack" style={{ marginTop: 20 }}>
    <ErrorBox error={error} onRetry={!settings ? () => setReload(value => value + 1) : undefined} />
    {loading ? <Loading label="正在读取 AI 服务设置…" /> : settings && drafts && <><div className="grid grid-2 company-ai-services employee-ai-services">
      {(['text', 'image'] as const).map(kind => {
        const saved = settings[kind], draft = drafts[kind], prefix = `employee-ai-${kind}`;
        const missing = missingPersonalFields(draft, saved.hasSecret);
        const result = results[kind]?.signature === signature(draft) ? results[kind]?.value : undefined;
        return <section className="card" id={prefix} key={kind}>
          <div className="card-heading"><div>
            <div className="employee-ai-title-row"><h2>{kind === 'text' ? '文字 AI 服务' : '生图服务'}</h2>
              <button id={`${prefix}-test`} className="button button-small" type="button" title="测试通过后自动保存本项个人配置，不切换使用来源" disabled={busy !== null} onClick={() => void testService(kind)}>{busy === kind ? '处理中…' : '测试'}</button>
            </div>
            <p className="card-subtitle">以下为自己的服务配置；测试只保存本项配置，不切换来源。支持 HTTP/HTTPS 地址和自定义端口；HTTP 会明文传输 API Key。</p>
            <p className="card-subtitle">当前使用：{saved.effectiveSource === 'company' ? '公司服务' : '自己的服务'}{!saved.shared && '（公司未开放共享）'}</p></div>
            <Pill status={saved.effectiveConfigured ? 'ready' : 'waiting'}>{saved.effectiveConfigured ? '当前服务已配置' : '当前服务待配置'}</Pill>
          </div>
          <div className="card-pad">
            <div className="form-grid">
              {([{ key: 'provider', label: '服务商（选填）' }, { key: 'baseUrl', label: 'API Base URL' }, { key: 'model', label: '模型' }] as const).map(field =>
                <div className="field" key={field.key}><label htmlFor={`${prefix}-${field.key}`}>{field.label}</label>
                  <input id={`${prefix}-${field.key}`} value={draft[field.key] ?? ''} onChange={event => update(kind, field.key, event.target.value)} />
                </div>)}
              <div className="field"><label htmlFor={`${prefix}-key`}>API Key</label>
                <input id={`${prefix}-key`} type="password" autoComplete="new-password" value={draft.apiKey ?? ''}
                  placeholder={saved.hasSecret ? '已配置；留空不变' : '未配置'} onChange={event => update(kind, 'apiKey', event.target.value)} />
              </div>
            </div>
            <div className="field-help" style={{ marginTop: 5 }}>测试通过后自动保存本项配置；{kind === 'image' ? '实际生成测试图' : '实际调用模型'}，可能产生费用</div>
            <div className="employee-ai-footer">
              <fieldset id={`${prefix}-source`} className="employee-ai-source" disabled={!saved.shared || busy !== null}>
                <legend>使用来源</legend>
                <div className="employee-ai-source-options">
                  {(['company', 'personal'] as const).map(source => <label key={source} htmlFor={`${prefix}-source-${source}`} title={source === 'personal' && missing.length ? `请先填写${missing.join('、')}` : undefined}>
                    <input id={`${prefix}-source-${source}`} className="check" type="radio" name={`${prefix}-source`} value={source}
                      checked={(saved.shared ? draft.source : 'personal') === source} disabled={source === 'personal' && missing.length > 0}
                      aria-describedby={source === 'personal' && saved.shared && missing.length ? `${prefix}-source-help` : undefined}
                      onChange={() => { if (source !== 'personal' || !missing.length) update(kind, 'source', source); }} />
                    {source === 'company' ? '公司服务' : '自己的服务'}
                  </label>)}
                </div>
                {saved.shared && missing.length > 0 && <span id={`${prefix}-source-help`} hidden>请先填写{missing.join('、')}。</span>}
              </fieldset>
            </div>
            {result && <div role="status" className="field-help" style={{ color: result.ok && result.saved ? '#067647' : '#b42318', marginTop: 8 }}>{result.ok ? '测试成功' : '测试失败'} · {result.durationMs} ms · {result.message}</div>}
          </div>
        </section>;
      })}
    </div>
      <section id="employee-ai-save-card" className="card card-pad employee-ai-save-bar" aria-label="保存模型默认值">
        {saveStatus && <div role="status" className={saveStatus.complete ? 'success-box' : 'notice'}>{saveStatus.message}</div>}
        <div className="form-actions">
          <button id="employee-ai-reload" className="button" type="button" disabled={busy !== null} onClick={() => setReload(value => value + 1)}>重新读取</button>
          <button id="employee-ai-save-defaults" className="button button-primary" type="button" disabled={busy !== null} onClick={() => void saveDefaults()}>{busy === 'save' ? '保存中…' : '保存默认值'}</button>
        </div>
      </section>
    </>}
  </div>;
}
